import { Button } from '@mui/material';
import Typography from '@mui/material/Typography';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useGlobalCore } from '../../../../core/modules/global';
import { CREATE_LOCATION_DIALOG } from '../../../../core/constants/dialog';
import { useMeCore } from '../../../../core/modules/me';
import { useCompanyCore } from '../../../../core/modules/company';
import { useLocationCore } from '../../../../core/modules/location';
import { useWarehouseCore } from '../../../../core/modules/warehouse';
import { useMasterCore } from '../../../../core/modules/master';
import { FilterDrawer, FilterField } from '../../../components/shared/FilterDrawer';

interface LocationFilters {
  code?: string;
  warehouse_code?: string;
  location_type_id?: string;
  channel_type_id?: string;
}

const Header = () => {
  const { user } = useMeCore();
  const { total, getLocations, locations } = useLocationCore();
  const { openDialog } = useGlobalCore();
  const { getWarehouses } = useWarehouseCore();
  const { getTypes, locationTypes, channelTypes } = useMasterCore();
  const { companyId } = useCompanyCore();

  const [filters, setFilters] = useState<LocationFilters>({});
  const [loading, setLoading] = useState(false);

  const canWrite = useMemo(() => user && (user.privileges.superAdmin || user.privileges.writeLocation), [user]);
  
  const openCreateDialog = useCallback(() => {
    if (companyId) {
      getWarehouses(companyId);
      getTypes(companyId, 'location');
      getTypes(companyId, 'channel');
    }
    openDialog(CREATE_LOCATION_DIALOG);
  }, [companyId, getTypes, getWarehouses, openDialog]);

  // Cargar datos necesarios al montar el componente
  useEffect(() => {
    if (companyId) {
      getWarehouses(companyId);
      getTypes(companyId, 'location');
      getTypes(companyId, 'channel');
      getLocations(companyId);
    }
  }, [companyId, getWarehouses, getTypes, getLocations]);

  const handleFiltersChange = useCallback((newFilters: LocationFilters) => {
    setFilters(newFilters);
  }, []);

  const handleApplyFilters = useCallback((appliedFilters?: LocationFilters) => {
    if (companyId) {
      setLoading(true);
      // Usar los filtros pasados como parámetro o los del estado actual
      const filtersToApply = appliedFilters || filters;
      // Actualizar el estado con los filtros aplicados
      if (appliedFilters) {
        setFilters(appliedFilters);
      }
      getLocations(companyId, filtersToApply, () => {
        setLoading(false);
      }, () => {
        setLoading(false);
      });
    }
  }, [companyId, filters, getLocations]);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    if (companyId) {
      setLoading(true);
      getLocations(companyId, {}, () => {
        setLoading(false);
      }, () => {
        setLoading(false);
      });
    }
  }, [companyId, getLocations]);

  const filterFields: FilterField[] = [
    {
      key: 'code',
      label: 'Código de ubicación',
      type: 'autocomplete',
      placeholder: 'Buscar por código de ubicación',
      autocompleteOptions: locations.map((loc) => ({
        id: loc.code,
        name: loc.code,
        description: loc.code,
      })),
    },
    {
      key: 'warehouse_code',
      label: 'Código de bodega',
      type: 'autocomplete',
      placeholder: 'Buscar por código de bodega',
      autocompleteOptions: Array.from(new Set(locations.map((loc: any) => loc.warehouse_code).filter(Boolean))).map((code) => ({
        id: code,
        name: code,
        description: code,
      })),
    },
    {
      key: 'location_type_id',
      label: 'Tipo de ubicación',
      type: 'select',
      options: locationTypes.map((type) => ({
        id: type.id,
        name: type.name || '',
        description: type.description,
      })),
    },
    {
      key: 'channel_type_id',
      label: 'Tipo de canal',
      type: 'select',
      options: channelTypes.map((channel) => ({
        id: channel.id,
        name: channel.name || '',
        description: channel.name,
      })),
    },
  ];

  return (
    <div className="p-24 sm:p-32 w-full border-b-1 bg-white header-components">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex flex-col items-center sm:items-start">
          <Typography className="text-24 md:text-32 font-extrabold tracking-tight leading-none">
            Ubicaciones
          </Typography>
          <Typography className="text-14 font-medium" color="text.secondary">
            {total} ubicacion/es
          </Typography>
        </div>
        <div className="flex w-full sm:w-auto items-center gap-4 mt-4 sm:mt-0">
          {/* Filtros Avanzados */}
          <FilterDrawer
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearFilters}
            fields={filterFields}
            loading={loading}
            disabled={!companyId}
          />

          {canWrite && (
            <Button
              variant="contained"
              color="secondary"
              className="my-auto mx-2"
              disabled={!companyId}
              onClick={openCreateDialog}
              sx={{
                minWidth: 120,
                height: 48,
                fontWeight: 700,
                fontSize: 16,
                borderRadius: 999,
                boxShadow: '0 2px 8px 0 rgba(80,80,200,0.08)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <AddCircleOutlineOutlinedIcon sx={{ fontSize: 22 }} />
              <span className="mx-8">Crear</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Header;
